import { optionalQueryParams } from "../apiEndpointHelpers";
import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  AdminV2BillingOfferDetail,
  AdminV2BillingOnboardingFlowDetail,
  JsonValue as AdminV2JsonValue,
  AdminV2RevenueCouponsResponse,
  AdminV2RevenueCouponUpsertMutationResponse,
  AdminV2RevenueOfferDraftMutationResponse,
  AdminV2RevenueOfferPublishMutationResponse,
  AdminV2RevenueOffersResponse,
  AdminV2RevenueOnboardingFlowDraftMutationResponse,
  AdminV2RevenueOnboardingFlowPublishMutationResponse,
  AdminV2RevenueOnboardingFlowsResponse,
  AdminV2RevenueRefundReason,
  AdminV2RevenueTransactionRefundMutationResponse,
} from "@/types/adminV2";

export const buildAdminV2RevenueCatalogEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2RevenueOffers: builder.query<
    AdminV2RevenueOffersResponse,
    {
      organisationId?: string;
      q?: string;
      status?: "draft" | "published" | "archived";
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/revenue/offers",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2RevenueOffers", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  getAdminV2RevenueOffer: builder.query<
    AdminV2BillingOfferDetail,
    { offerId: string }
  >({
    query: ({ offerId }) => ({
      url: `/admin/v2/revenue/offers/${offerId}`,
    }),
    providesTags: (_result, _error, { offerId }) => [
      { type: "AdminV2RevenueOffers", id: offerId },
    ],
    keepUnusedDataFor: 10,
  }),

  createAdminV2RevenueOffer: builder.mutation<
    AdminV2BillingOfferDetail,
    {
      organisationId: string;
      name: string;
      offerKey?: string;
      description?: string;
      stripeProductId?: string;
      onboardingFlowId?: string;
      offerConfig?: AdminV2JsonValue;
      priceMappings?: Array<{
        stripeProductId: string;
        stripePriceId: string;
        priceLookupKey?: string;
        currency: string;
        unitAmountCents: number;
        recurringInterval?: string;
        recurringIntervalCount?: number;
        isPrimary?: boolean;
        metadata?: AdminV2JsonValue;
      }>;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/revenue/offers",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2RevenueOffers", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  updateAdminV2RevenueOffer: builder.mutation<
    AdminV2RevenueOfferDraftMutationResponse,
    {
      offerId: string;
      offerKey?: string;
      name?: string;
      description?: string;
      stripeProductId?: string;
      onboardingFlowId?: string;
      offerConfig?: AdminV2JsonValue;
      approvalRequestId?: string;
      priceMappings?: Array<{
        stripeProductId: string;
        stripePriceId: string;
        priceLookupKey?: string;
        currency: string;
        unitAmountCents: number;
        recurringInterval?: string;
        recurringIntervalCount?: number;
        isPrimary?: boolean;
        metadata?: AdminV2JsonValue;
      }>;
    }
  >({
    query: ({ offerId, ...body }) => ({
      url: `/admin/v2/revenue/offers/${offerId}`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { offerId }) => [
      { type: "AdminV2RevenueOffers", id: "LIST" },
      { type: "AdminV2RevenueOffers", id: offerId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  publishAdminV2RevenueOffer: builder.mutation<
    AdminV2RevenueOfferPublishMutationResponse,
    {
      offerId: string;
      syncStripe?: boolean;
      approvalRequestId?: string;
    }
  >({
    query: ({ offerId, ...body }) => ({
      url: `/admin/v2/revenue/offers/${offerId}/publish`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: (_result, _error, { offerId }) => [
      { type: "AdminV2RevenueOffers", id: "LIST" },
      { type: "AdminV2RevenueOffers", id: offerId },
      { type: "AdminV2AuditLog", id: "LIST" },
      { type: "AdminV2Approvals", id: "LIST" },
    ],
  }),

  getAdminV2RevenueOnboardingFlows: builder.query<
    AdminV2RevenueOnboardingFlowsResponse,
    {
      organisationId?: string;
      q?: string;
      status?: "draft" | "published" | "archived";
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/revenue/onboarding-flows",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2RevenueOnboardingFlows", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  getAdminV2RevenueOnboardingFlow: builder.query<
    AdminV2BillingOnboardingFlowDetail,
    { flowId: string }
  >({
    query: ({ flowId }) => ({
      url: `/admin/v2/revenue/onboarding-flows/${flowId}`,
    }),
    providesTags: (_result, _error, { flowId }) => [
      { type: "AdminV2RevenueOnboardingFlows", id: flowId },
    ],
    keepUnusedDataFor: 10,
  }),

  createAdminV2RevenueOnboardingFlow: builder.mutation<
    AdminV2BillingOnboardingFlowDetail,
    {
      organisationId: string;
      name: string;
      flowKey?: string;
      description?: string;
      schema?: AdminV2JsonValue;
      settings?: AdminV2JsonValue;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/revenue/onboarding-flows",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2RevenueOnboardingFlows", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  updateAdminV2RevenueOnboardingFlow: builder.mutation<
    AdminV2RevenueOnboardingFlowDraftMutationResponse,
    {
      flowId: string;
      flowKey?: string;
      name?: string;
      description?: string;
      schema?: AdminV2JsonValue;
      settings?: AdminV2JsonValue;
      approvalRequestId?: string;
    }
  >({
    query: ({ flowId, ...body }) => ({
      url: `/admin/v2/revenue/onboarding-flows/${flowId}`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { flowId }) => [
      { type: "AdminV2RevenueOnboardingFlows", id: "LIST" },
      { type: "AdminV2RevenueOnboardingFlows", id: flowId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  publishAdminV2RevenueOnboardingFlow: builder.mutation<
    AdminV2RevenueOnboardingFlowPublishMutationResponse,
    {
      flowId: string;
      approvalRequestId?: string;
    }
  >({
    query: ({ flowId, ...body }) => ({
      url: `/admin/v2/revenue/onboarding-flows/${flowId}/publish`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: (_result, _error, { flowId }) => [
      { type: "AdminV2RevenueOnboardingFlows", id: "LIST" },
      { type: "AdminV2RevenueOnboardingFlows", id: flowId },
      { type: "AdminV2AuditLog", id: "LIST" },
      { type: "AdminV2Approvals", id: "LIST" },
    ],
  }),

  getAdminV2RevenueCoupons: builder.query<
    AdminV2RevenueCouponsResponse,
    {
      organisationId?: string;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/revenue/coupons",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2RevenueCoupons", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  upsertAdminV2RevenueCoupon: builder.mutation<
    AdminV2RevenueCouponUpsertMutationResponse,
    {
      couponId?: string;
      organisationId: string;
      couponKey: string;
      name: string;
      stripeCouponId?: string;
      stripePromotionCodeId?: string;
      discountType: "percent" | "amount";
      percentOff?: number | null;
      amountOffCents?: number | null;
      currency?: string | null;
      duration?: "once" | "repeating" | "forever";
      durationInMonths?: number | null;
      expiresAt?: string | null;
      status?: "active" | "inactive" | "archived";
      metadata?: AdminV2JsonValue;
      syncStripe?: boolean;
      approvalRequestId?: string;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/revenue/coupons",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2RevenueCoupons", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
      { type: "AdminV2Approvals", id: "LIST" },
    ],
  }),

  refundAdminV2RevenueTransaction: builder.mutation<
    AdminV2RevenueTransactionRefundMutationResponse,
    {
      transactionId: string;
      organisationId: string;
      approvalRequestId?: string;
      amountCents?: number;
      reason?: AdminV2RevenueRefundReason;
      metadata?: AdminV2JsonValue;
    }
  >({
    query: ({ transactionId, ...body }) => ({
      url: `/admin/v2/revenue/transactions/${transactionId}/refund`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: (_result, _error, { transactionId }) => [
      { type: "AdminV2RevenueTransactions", id: "LIST" },
      { type: "AdminV2RevenueTransactions", id: transactionId },
      { type: "AdminV2AuditLog", id: "LIST" },
      { type: "AdminV2Approvals", id: "LIST" },
    ],
  }),
});
